import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertLevel, Button, Text } from '@vizit/atoms';
import { Form, FormTextField } from '@vizit/forms';
import { Heading, HeadingVariant, RouterLink } from '@vizit/molecules';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

enum ResetPasswordError {
  Default = 'default',
  InvalidToken = 'invalid-token',
}

type ResetPasswordFormSchema = {
  password: string;
  confirmPassword: string;
};

interface ResetPasswordFormProps {
  /**
   * Whether the form has a submission error when setting the password.
   */
  hasSubmissionError: null | ResetPasswordError;
  /**
   * The function to call when the form is submitted.
   */
  onSubmit: (data: ResetPasswordFormSchema) => Promise<void>;
}

function ResetPasswordForm(props: ResetPasswordFormProps) {
  const { hasSubmissionError, onSubmit } = props;
  // Form and validation schema
  const formSchema = yup.object().shape({
    password: yup
      .string()
      .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/, 'Please create a stronger password.')
      .required('Please enter your password.'),
    confirmPassword: yup
      .string()
      .test('passwords-match', 'Passwords must match', function (value) {
        return this.parent.password === value;
      })
      .required('Please confirm your password.'),
  });

  const { control, handleSubmit } = useForm<ResetPasswordFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  return (
    <div className="flex-grow flex flex-col justify-center items-center">
      <div className="justify-center flex flex-col items-center gap-14 p-4">
        <div className="flex flex-col items-center justify-center gap-4">
          <Heading variant={HeadingVariant.H6}>Reset password</Heading>
          <div className="min-h-[70px]">
            {hasSubmissionError ? (
              <Alert className="min-w-[316px] animate-fade-in" level={AlertLevel.Error}>
                <Alert.Description>
                  {hasSubmissionError === ResetPasswordError.InvalidToken ? (
                    <>
                      Password reset has expired, use{' '}
                      <RouterLink className="text-sm" to="/forgot-password">
                        forgot password
                      </RouterLink>{' '}
                      to request a new reset link.
                    </>
                  ) : (
                    'Something went wrong. Please try again.'
                  )}
                </Alert.Description>
              </Alert>
            ) : (
              <Text
                className="data-[error]:animate-fade-out max-w-[380px] text-center"
                data-error={hasSubmissionError || undefined}
                showEllipsis={false}
              >
                Enter a new password below to regain access to your account.
              </Text>
            )}
          </div>
        </div>
        <Form className="gap-4 min-w-[380px]" onSubmit={handleSubmit(onSubmit)}>
          <FormTextField isRequired type="password" label="Password" control={control} name="password" />
          <FormTextField isRequired type="password" label="Confirm Password" control={control} name="confirmPassword" />
          <Button type="submit">Reset</Button>
        </Form>
      </div>
    </div>
  );
}

export { ResetPasswordForm, ResetPasswordError };
export type { ResetPasswordFormSchema };
